import React, { useEffect } from 'react';
import { Routes, Route, useMatch, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { toast } from 'sonner';
import '@trellixio/roaster-coffee/theme-coffee.css';
import { useIsFetching } from '@tanstack/react-query';
import ProgressiveLoader from 'components/shared/ProgressiveLoader';
import Sidebar from 'components/shared/Sidebar';
import ProtectedRoute from 'components/shared/ProtectedRoute';
import AuthSidebar from 'components/shared/AuthSidebar';
import { SidebarProvider } from 'components/ui/sidebar';
import { Urls } from 'data';
import { useGetUser } from 'hooks';
import { Storage, onLogout } from 'helpers';
import Locales from 'locales';
import {
  ForgotPassword,
  Login,
  Logout,
  Agency,
  Invoice,
  Preferences,
  Authorization,
  Dashboard,
  ResetPassword,
  Trip,
  User,
  UserProfile,
  Error404,
  LegalMention,
  Cgu,
  CookiePolicy,
  AgencyAssignation,
  Profile,
  Register,
  Group,
  Home,
} from 'pages';

const CONST_LEGAL_DOWNLOADED = 'legal_downloaded';
const CONST_LEGAL_TOAST_DISPLAYED = 'legal_toast_displayed';

const App = () => {
  const storage = new Storage(localStorage, 'eHadj-');
  const isLegalDownloaded = storage.getData(CONST_LEGAL_DOWNLOADED) === true;
  const isLegalToastDisplayed = storage.getData(CONST_LEGAL_TOAST_DISPLAYED) === true;
  const legalToastDuration = 30000;

  const {
    isConnected,
    user,
    isSuperAdmin,
    isBank,
    isAGLO,
    isAgency,
    isAgencyPrimary,
    isPrimaryUser,
    isMS,
    isDEI,
    isMAEC,
    isPilgrim,
    canAccessDashboard,
    activeOrganizationId,
    activeRole,
  } = useGetUser();
  const isFetching = useIsFetching();
  const agloPageKey = isAGLO ? `${user?.id}:${activeOrganizationId}:${activeRole}` : 'other-role';
  const location = useLocation();

  const hideSidebarPages = [
    Urls.base,
    Urls.cgu,
    Urls.legal_mention,
    Urls.cookie_policy,
    Urls.profile.base,
    Urls.front.logout,
  ];

  const isPilgrimStandaloneRoute =
    isPilgrim &&
    (location.pathname === Urls.userProfile ||
      location.pathname === Urls.profile.base ||
      location.pathname.startsWith(`${Urls.profile.base}/`));
  const hideSidebar = hideSidebarPages.filter((URL) => useMatch(URL)).length > 0 || isPilgrimStandaloneRoute;

  const getPageTitle = () => {
    let title = '';

    switch (location.pathname) {
      case Urls.agency:
        title = Locales.base.sidebar.agencies;
        break;
      case Urls.apikey:
        title = Locales.base.sidebar.api_key;
        break;
      case Urls.authorization:
        title = Locales.base.sidebar.authorization;
        break;
      case Urls.preferences:
        title = Locales.base.sidebar.preferences;
        break;
      case Urls.cgu:
        title = Locales.base.sidebar.cgu;
        break;
      case Urls.cookie_policy:
        title = Locales.cookie.title;
        break;
      case Urls.dashboard:
        title = Locales.base.sidebar.dashboard;
        break;
      case Urls.invoice:
        title = Locales.base.sidebar.invoices;
        break;
      case Urls.legal_mention:
        title = Locales.base.sidebar.legal_mention;
        break;
      case Urls.front.login:
        title = Locales.auth.login;
        break;
      case Urls.front.password.forgot:
        title = Locales.auth.forgot_pwd.title;
        break;
      case Urls.front.password.reset:
        title = Locales.auth.reset_password_title;
        break;
      case Urls.trip:
        title = Locales.base.sidebar.trips;
        break;
      case Urls.user:
        title = Locales.base.sidebar.users;
        break;
      case Urls.userProfile:
        title = Locales.base.sidebar.profile;
        break;
      case Urls.assignation:
        title = 'Assignation';
        break;
      case Urls.group:
        title = 'Groupes';
        break;
      case Urls.register:
        title = 'Inscription';
        break;
      default:
        break;
    }
    return title !== '' ? `${title}${' - Bénin Hadj'}` : 'Bénin Hadj';
  };

  useEffect(() => {
    if (isConnected && !isLegalToastDisplayed) {
      toast(
        Locales.formatString(Locales.base.legals.download, {
          consent_receipt: (
            <strong>
              <a
                href={`${process.env.PUBLIC_URL}pdfs/legal-consent.pdf`}
                download={Locales.base.legals.document_name}
                target="_blank"
                rel="noopener noreferrer"
              >
                {Locales.base.legals.consent_receipt}
              </a>
            </strong>
          ),
        }),
        {
          duration: legalToastDuration,
          closeButton: true,
          onDismiss: () => storage.set(CONST_LEGAL_TOAST_DISPLAYED, true),
        }
      );

      setTimeout(() => {
        storage.set(CONST_LEGAL_TOAST_DISPLAYED, true);
      }, legalToastDuration);
    }

    // Force disconnect all user that did not accept Legals
    if (isConnected && !isLegalDownloaded) {
      onLogout();
    }
  }, [isConnected, user]);

  return (
    <>
      <Helmet>
        <title>{getPageTitle()}</title>
      </Helmet>

      {isFetching ? <ProgressiveLoader /> : null}

      {!isConnected && !hideSidebar && <AuthSidebar />}

      {isConnected && !hideSidebar && (
        <SidebarProvider>
          <Sidebar />
        </SidebarProvider>
      )}

      <div
        className={`flex-1 min-h-screen transition-all duration-500 ease-in-out ${
          !hideSidebar
            ? isConnected
              ? 'pt-16 lg:ml-[224px] lg:pt-0 collapsed:lg:ml-24'
              : 'lg:ml-[min(27vw,450px)]'
            : ''
        }`}
      >
        <Routes>
          <Route index element={<Home />} />

          <Route element={<ProtectedRoute isAllowed={!isConnected} />}>
            <Route path={Urls.register} element={<Register />} />
            <Route path={Urls.front.login} element={<Login />} />
            <Route path={Urls.front.password.forgot} element={<ForgotPassword />} />
            <Route path={Urls.front.password.reset} element={<ResetPassword />} />
          </Route>

          <Route path={Urls.front.logout} element={<Logout />} />
          <Route path={Urls.cgu} element={<Cgu />} />
          <Route path={Urls.legal_mention} element={<LegalMention />} />
          <Route path={Urls.cookie_policy} element={<CookiePolicy />} />

          <Route element={<ProtectedRoute isAllowed={isConnected} />}>
            {/* <Route
              path={Urls.apikey}
              element={
                <ProtectedRoute isAllowed={isBank}>
                  <ApiKey />
                </ProtectedRoute>
              }
            /> */}
            <Route
              path={Urls.invoice}
              element={
                <ProtectedRoute isAllowed={isAGLO || isBank || isAgency}>
                  <Invoice key={agloPageKey} />
                </ProtectedRoute>
              }
            />
            <Route
              path={Urls.agency}
              element={
                <ProtectedRoute isAllowed={isAGLO}>
                  <Agency />
                </ProtectedRoute>
              }
            />
            <Route
              path={Urls.preferences}
              element={
                <ProtectedRoute isAllowed={isSuperAdmin || isAGLO}>
                  <Preferences />
                </ProtectedRoute>
              }
            />
            <Route
              path={Urls.trip}
              element={
                <ProtectedRoute isAllowed={isAgency || isAGLO || isMS || isDEI || isMAEC}>
                  <Trip key={agloPageKey} />
                </ProtectedRoute>
              }
            />
            <Route
              path={Urls.authorization}
              element={
                <ProtectedRoute isAllowed={isAgencyPrimary}>
                  <Authorization />
                </ProtectedRoute>
              }
            />
            <Route
              path={Urls.dashboard}
              element={
                <ProtectedRoute isAllowed={canAccessDashboard}>
                  <Dashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path={Urls.user}
              element={
                <ProtectedRoute isAllowed={isPrimaryUser}>
                  <User />
                </ProtectedRoute>
              }
            />
            <Route path={Urls.userProfile} element={<UserProfile />} />
            <Route
              path={Urls.assignation}
              element={
                <ProtectedRoute isAllowed={isAGLO}>
                  <AgencyAssignation />
                </ProtectedRoute>
              }
            />

            <Route
              path={Urls.profile.base}
              element={
                <ProtectedRoute isAllowed={isPilgrim}>
                  <Profile />
                </ProtectedRoute>
              }
            />
            <Route
              path={Urls.profile.object}
              element={
                <ProtectedRoute isAllowed={isPilgrim}>
                  <Profile />
                </ProtectedRoute>
              }
            />

            <Route
              path={Urls.group}
              element={
                <ProtectedRoute isAllowed={isAgency || isAGLO}>
                  <Group key={agloPageKey} />
                </ProtectedRoute>
              }
            />
            <Route path="*" element={<Error404 />} />
          </Route>
        </Routes>
      </div>
    </>
  );
};

export default App;
